import React, {useEffect, useState} from 'react';
import styles from './ListOfFilesUploaded.module.css';
import {
    DetailsList,
    FontIcon,
    IColumn,
    IconButton,
    IContextualMenuItem,
    IContextualMenuProps,
    mergeStyles,
    mergeStyleSets,
    Pivot,
    PivotItem,
    SelectionMode,
    Spinner,
    Stack,
    StackItem,
    TooltipOverflowMode
} from "@fluentui/react";
import {IDropdownOption} from "@fluentui/react/lib/Dropdown";
import strings from '../../loc/strings';
import {
    _listOfFilesUploadedColumns,
    ColumnFieldName,
    Tab
} from "./ListOfFIlesUploadedUtils";
import {fetchFilesUploaded} from "../../services/ListOfFilesUploadedServices/ListOfFilesUploadedService";
import {FilesUploaded, FileUploadsForm} from "../../model/FilesUploaded";
import {SORT_ORDER_ASC, SORT_ORDER_DESC} from "../../constants/EiopaFnPConstants";
import EiopaFnPIcons from "../EiopaFnPIcons/EiopaFnPIcons";
import Pagination from "../Pagination/Pagination";
import {useAuth} from "react-oidc-context";
import {TooltipHost} from "@fluentui/react/lib/Tooltip";

interface ListOfFilesUploadedProps {}

const iconClass = mergeStyles({
    fontSize: 26,
    height: 20,
    width: 20,
});

const iconBlock = mergeStyles({
    fontSize: 26,
    height: 20,
    width: 20,
});

const iconProgress = mergeStyles({
    fontSize: 24,
    height: 20,
    width: 20,
});

const iconError = mergeStyles({
    fontSize: 24,
    height: 20,
    width: 20,
});

const classNames = mergeStyleSets({
    grey: [{ color: '#797775' }, iconProgress],
    red: [{ color: '#D83B01' }, iconBlock],
    greenSuccess: [{ color: '#107C10' }, iconClass],
    greenError: [{ color: '#107C10' }, iconError],
});

function ListOfFilesUploaded(props: Readonly<ListOfFilesUploadedProps>) {
    const auth = useAuth();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedTab, setSelectedTab] = useState<string>(Tab.Normal);
    const [columnsState, setColumnsState] = useState<IColumn[]>(_listOfFilesUploadedColumns);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [items, setItems] = useState<FilesUploaded[]>([]);
    const [form, setForm] = useState<FileUploadsForm>({
        tab: Tab.Normal,
        itemsPerPage: itemsPerPage,
        page: currentPage,
        sortBy: columnsState[2].key as string,
        sortOrder: 'desc'
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetchFilesUploaded(form, auth.user?.id_token);
                setIsLoading(false);
                setItems(response);
                setTotalItems(response[0].totalItems)
            } catch (error) {
                setIsLoading(false);
            }
        };

        fetchData();

    }, [form]);

    const _onColumnHeaderClick = (ev?: React.MouseEvent<HTMLElement>, column?: IColumn) => {
        if(column === undefined || column.fieldName === ColumnFieldName.Actions) return;

        setIsLoading(true);

        let isSortedDescending = column.isSortedDescending;

        if (column.isSorted) {
            isSortedDescending = !isSortedDescending;
        }

        let columns = columnsState.map(col => {
            col.isSorted = col.key === column.key;
            if (col.isSorted) {
                col.isSortedDescending = isSortedDescending;
            }
            return col;
        });

        setCurrentPage(1);

        if (column.fieldName !== ColumnFieldName.Actions ) {
            setForm({
                ...form,
                page: 1,
                sortBy: columnsState.filter(c => c.isSorted)[0].key as string,
                sortOrder: columnsState.filter(c => c.isSorted)[0].isSortedDescending ? SORT_ORDER_DESC : SORT_ORDER_ASC
            });
        }
    };

    function _renderItemColumn(item?: FilesUploaded, index?: number, column?: IColumn) {
        if (!item || !column) return;

        const fieldValue = item[column.fieldName as keyof typeof item];

        switch (column.fieldName) {
            case ColumnFieldName.UploadStatus:
                return renderUploadStatusCell(item);
            case ColumnFieldName.Actions:
                return renderActionsCell(item);
            case ColumnFieldName.UploadDateTime:
                if (typeof fieldValue === 'string' || typeof fieldValue === 'number') {
                    return renderUploadDateTimeCell(new Date(fieldValue));
                } else {
                    return renderUploadDateTimeCell(fieldValue);
                }
            case ColumnFieldName.NumberOfImpactedIdsInCsv:
                let impactedIds;
                if (item.impactedIds === null) {
                    return renderDefaultCell(null);
                }
                impactedIds = item.impactedIds.toString() === 'N/A' ? 'N/A' :  (item.totalRows - item.impactedIds) + '/' + item.impactedIds;
                return renderDefaultCell(impactedIds);
            default:
                return renderDefaultCell(fieldValue);
        }
    }

    function renderUploadStatusCell(item: FilesUploaded) {
        switch (item.uploadStatus) {
            case 0:
                return (
                    <TooltipHost content="In Progress">
                        <FontIcon aria-label="ProgressRingDots" iconName="ProgressRingDots" className={classNames.grey} />
                    </TooltipHost>
                );
            case 1:
                return (
                    <TooltipHost content={item.validationError}>
                        <FontIcon aria-label="StatusCircleBlock" iconName="StatusCircleBlock" className={classNames.red} />
                    </TooltipHost>
                );
            case 2:
                return (
                    <TooltipHost content={item.validationError}>
                        <FontIcon aria-label="CheckMark" iconName="CheckMark" className={classNames.greenSuccess} />
                    </TooltipHost>
                );
            case 3:
                return (
                    <TooltipHost content="Successfully Uploaded">
                        <FontIcon aria-label="CheckMark" iconName="CheckMark" className={classNames.greenSuccess} />
                    </TooltipHost>
                );
            case 4:
                return (
                    <TooltipHost content={item.validationError}>
                        <FontIcon aria-label="Error" iconName="Error" className={classNames.greenError} />
                    </TooltipHost>
                );
            default:
                return null;
        }
    }

    function renderActionsCell(item: FilesUploaded) {
        return _actionsButton(item);
    }

    function renderUploadDateTimeCell(fieldValue: Date | null) {
        if (fieldValue === null) return "";

        // format the date time to YYYY/MM/DD HH:MM:SS
        const dateObj = new Date(fieldValue);
        const year = dateObj.getFullYear().toString();
        const month = (dateObj.getMonth() + 1 < 10 ? '0' : '') + (dateObj.getMonth() + 1).toString();
        const date = (dateObj.getDate() < 10 ? '0' : '') + dateObj.getDate().toString();
        const hours = (dateObj.getHours() < 10 ? '0' : '') + dateObj.getHours().toString();
        const minutes = (dateObj.getMinutes() < 10 ? '0' : '') + dateObj.getMinutes().toString();
        const seconds = (dateObj.getSeconds() < 10 ? '0' : '') + dateObj.getSeconds().toString();
        const date_String = `${year}/${month}/${date} ${hours}:${minutes}:${seconds}`;

        return (
            <TooltipHost content={date_String} overflowMode={TooltipOverflowMode.Parent}>
                <span>{date_String}</span>
            </TooltipHost>
        );
    }

    function renderDefaultCell(fieldValue: any) {
        const content = fieldValue !== null ? fieldValue.toString() : "";
        return (
            <TooltipHost content={content} overflowMode={TooltipOverflowMode.Parent}>
                <span>{content}</span>
            </TooltipHost>
        );
    }

    function _actionsButton(item: FilesUploaded) {
        if (item.uploadStatus === 2 || item.uploadStatus === 3 || item.uploadStatus === 4) {
            const menuProps = (): IContextualMenuProps => {
                return {
                    items: _viewDetails(item),
                    directionalHintFixed: false,
                    className: styles.actionsPanel
                } as IContextualMenuProps;
            }

            return (
                <IconButton
                    menuProps={menuProps()}
                    onRenderIcon={_onRenderActionsIcon}
                    onRenderMenuIcon={_onRenderActionsMenuIcon}
                    title="Actions"
                    ariaLabel="Actions"
                    className="file-actions-button"
                />
            )
        } else {
            return <></>
        }
    }

    function _onRenderActionsIcon() {
        return <EiopaFnPIcons iconName={"actions"} iconColor={"#0078D4"}/>;
    }

    function _onRenderActionsMenuIcon() {
        return <></>;
    }

    function _onRenderViewDetailsIcon() {
        return <EiopaFnPIcons iconName={"viewDetails"} iconColor={"#0078D4"}/>;
    }

    function _viewDetails(item: FilesUploaded): IContextualMenuItem[] {
        const filesUploaded: IContextualMenuItem[] = [];

        filesUploaded.push({
            className: (item.fileName)+'-view-file',
            key: 'view',
            text: strings.viewDetails,
            onRenderIcon: () => _onRenderViewDetailsIcon(),
            iconProps: {},
            onClick: () => {
                const baseUrl = window.location.origin;
                const encodedFileName = encodeURIComponent(item.fileName);
                const encodedEncryptedId = encodeURIComponent(item.encryptedId);
                const url = `${baseUrl}/manage-system-data/view-validation-results/${encodedFileName}/${encodedEncryptedId}`;
                // Create a temporary anchor element with rel="opener"
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.rel = 'opener';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        });

        return filesUploaded;
    }

    function _onPageChange(selectedItem: number) {
        setIsLoading(true);

        setCurrentPage(selectedItem);

        setForm(prevState => ({
            ...prevState,
            page: selectedItem
        }));
    }

    function _onChangeItemsPerPage(event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) {
        setIsLoading(true);

        const itemsPerPage = item.key as number;

        setItemsPerPage(item.key as number);
        setCurrentPage(1);

        setForm(prevState => ({
            ...prevState,
            itemsPerPage: itemsPerPage,
            page: 1
        }));
    }

    const getTabId = (itemKey: string) => { return `Selected_${itemKey}`; };

    const handleTabClick = (item?: PivotItem) => {
        if (item) {
            const newTab = item.props.itemKey!;
            if (newTab === selectedTab) {
                return;
            }
            setIsLoading(true);
            setSelectedTab(newTab);

            // Find the UploadDate column
            const uploadDateColumn = columnsState.find(col => col.fieldName === ColumnFieldName.UploadDateTime);
            if (uploadDateColumn) {
                // Update sorting properties for the UploadDate column
                uploadDateColumn.isSorted = true;
                uploadDateColumn.isSortedDescending = true;

                // Reset sorting properties for other columns
                columnsState.forEach(col => {
                    if (col.fieldName !== ColumnFieldName.UploadDateTime) {
                        col.isSorted = false;
                        col.isSortedDescending = false;
                    }
                });

                setColumnsState([...columnsState]);
            }

            setForm(prevState => ({
                ...prevState,
                tab: newTab,
                page: 1,
                sortBy: columnsState[2].key as string,
                sortOrder: 'desc'
            }));
            setCurrentPage(1);
        }
    }


    return (
        <Stack>
            <StackItem className={styles.ListOfFilesUploadedHeader}>
                {strings.listOfFilesUploadedLabel}
            </StackItem>
            <Pivot
                aria-label="Main body pivot"
                selectedKey={selectedTab}
                // eslint-disable-next-line react/jsx-no-bind
                onLinkClick={handleTabClick}
                headersOnly={true}
                getTabId={getTabId}
                linkFormat={"links"}
            >
                <PivotItem
                    itemKey={Tab.Normal}
                    headerText={strings.normalTabLabel}
                />
                <PivotItem
                    itemKey={Tab.Historical}
                    headerText={strings.historicalTabLabel}
                />
            </Pivot>
            <StackItem>
                {
                    isLoading ?
                        <Spinner label={strings.requestResultLoading} size={2} styles={{root: {padding: 20}}}></Spinner>
                        :
                        <Stack>
                            <StackItem>
                                {(items.length === 0 || (items.length === 1 && items[0].id === 0)) ?
                                    <div>{strings.requestNoResults}</div>
                                    :
                                    <DetailsList
                                        layoutMode={1}
                                        columns={_listOfFilesUploadedColumns.map((column) => ({
                                            ...column,
                                            onRenderHeader: () => (
                                                <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={column.name}>{column.name}</TooltipHost>
                                            ),
                                        }))}
                                        items={items}
                                        selectionMode={SelectionMode.none}
                                        selectionPreservedOnEmptyClick={true}
                                        onRenderItemColumn={_renderItemColumn}
                                        onColumnHeaderClick={(event, column) => _onColumnHeaderClick(event, column)}
                                    />
                                }
                            </StackItem>
                            <StackItem>
                                <Pagination
                                    currentPage={currentPage}
                                    onPageChange={_onPageChange}
                                    itemsPerPage={itemsPerPage}
                                    onChangeItemsPerPage={_onChangeItemsPerPage}
                                    totalItems={totalItems}
                                />
                            </StackItem>
                        </Stack>
                }
            </StackItem>
        </Stack>
    );
}

export default ListOfFilesUploaded;
